/*
 * ProgressBar.jsx
 * ---------------
 * Displays checklist completion progress as a thin animated bar.
 * Shows percentage complete and item count.
 * Color shifts from green to gold at 100% completion.
 * Used on ChecklistCard and ChecklistDetail.
 */

import React, { useEffect, useState } from "react";

// ============================================
// MAIN EXPORT
// ============================================
export default function ProgressBar({
  total,
  completed,
  showLabel = true,
  showCount = true,
  height = 6,
  animate = true
}) {
  const [width, setWidth] = useState(0);
  const percentage = total === 0 ? 0 : Math.round((completed / total) * 100);
  const isComplete = percentage === 100;

  // ============================================
  // ANIMATE IN ON MOUNT AND ON CHANGE
  // ============================================
  useEffect(() => {
    if (!animate) {
      setWidth(percentage);
      return;
    }
    // Brief delay so the animation is visible on mount
    const timer = setTimeout(() => setWidth(percentage), 50);
    return () => clearTimeout(timer);
  }, [percentage, animate]);

  return (
    <div style={{ width: "100%" }}>

      {/* Label row */}
      {(showLabel || showCount) && (
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "var(--space-2)"
        }}>

          {/* Percentage label */}
          {showLabel && (
            <span style={{
              fontFamily: "var(--font-body)",
              fontSize: "12px",
              fontWeight: 600,
              color: isComplete
                ? "var(--accent-gold)"
                : "var(--accent-green)",
              transition: "color var(--transition-base)"
            }}>
              {isComplete ? "Complete! 🎉" : `${percentage}%`}
            </span>
          )}

          {/* Item count */}
          {showCount && (
            <span style={{
              fontFamily: "var(--font-body)",
              fontSize: "12px",
              color: "var(--text-muted)"
            }}>
              {completed} / {total}
            </span>
          )}
        </div>
      )}

      {/* Track */}
      <div style={{
        width: "100%",
        height: `${height}px`,
        background: "var(--bg-surface-2)",
        borderRadius: "100px",
        overflow: "hidden",
        border: "1px solid var(--border)"
      }}>

        {/* Fill */}
        <div style={{
          height: "100%",
          width: `${width}%`,
          borderRadius: "100px",
          background: isComplete
            ? "linear-gradient(90deg, var(--accent-green), var(--accent-gold))"
            : "var(--accent-green)",
          transition: animate
            ? "width 600ms cubic-bezier(0.4, 0, 0.2, 1), background 400ms ease"
            : "none",
          boxShadow: isComplete
            ? "0 0 8px rgba(201, 144, 26, 0.4)"
            : "none"
        }} />
      </div>
    </div>
  );
}